import { useState, useEffect } from "react";
import styles from "./MarketPlaceDetail.module.css";
import { Check } from "@material-ui/icons";
import UnitPriceT2Form from "../MarketPlacePricing/UnitPriceT2Form";
import T2RangeEdit from "../MarketPlacePricing/T2RangeEdit";

function MarketPlaceT2({
  selected,
  selectInterval,
  plans,
  setPlans,
  features,
  selectedVendor,
}) {
  const [quantity, setQuantity] = useState(0);
  const [steps, setStep] = useState(1);
  const [rangeData, setRangeData] = useState([]);
  const [unitIndex, setUnitIndex] = useState(0);

  useEffect(() => {
    const firstPlan = plans.find(
      (p) => p.typeId === selected && p.interval === selectInterval,
    );

    if (!firstPlan || !Array.isArray(firstPlan.unit_ids)) return;

    const ranges = firstPlan.unit_ids.map((u) => ({
      StartRange: u.StartRange,
      EndRange: u.EndRange,
      Steps: u.Steps || 1,
    }));

    setRangeData(ranges);
  }, [plans, selected, selectInterval]);

  function getAllowedValue(q, ranges) {
    q = Number(q);
    for (let i = 0; i < ranges.length; i++) {
      const { StartRange, EndRange, Steps } = ranges[i];
      const stepValue = Steps && Steps > 0 ? Steps : 1; // normalize
      if (q >= StartRange && q <= EndRange) {
        const offset = (q - StartRange) % stepValue;

        if (offset === 0) {
          return {
            q,
            steps: stepValue,
            index: i,
          };
        } else {
          const nextAllowed = q + (stepValue - offset);

          if (nextAllowed <= EndRange) {
            return {
              q: nextAllowed,
              steps: stepValue,
              index: i,
            };
          } else if (ranges[i + 1]) {
            const { StartRange: nextStart, steps: nextSteps } = ranges[i + 1];
            const nextStepValue = nextSteps && nextSteps > 0 ? nextSteps : 1;
            const correctedQ = nextStart + nextStepValue;
            return {
              q: correctedQ,
              steps: nextStepValue,
              index: i + 1,
            };
          } else {
            return {
              q: EndRange,
              steps: stepValue,
              index: i,
            };
          }
        }
      }
    }
    // Case: q < first range
    if (q < ranges[0].StartRange) {
      const { StartRange, Steps } = ranges[0];
      const stepValue = Steps && Steps > 0 ? Steps : 1;
      const correctedQ = StartRange + stepValue;
      return {
        q: correctedQ,
        steps: stepValue,
        index: 0,
      };
    }
    // Case: q > last range
    const last = ranges[ranges.length - 1];
    const stepValue = last.Steps && last.Steps > 0 ? last.Steps : 1;
    return {
      q: last.EndRange,
      steps: stepValue,
      index: ranges.length - 1,
    };
  }

  const handleQuantityChange = (e) => {
    if (rangeData.length) {
      let res = getAllowedValue(e.target.value, rangeData);
      setQuantity(res.q);
      setStep(res.steps);
      setUnitIndex(res.index);
    }
  };

  const updatePlanUnitIds = (planId, unitIdsArray) => {
    setPlans((prev) =>
      prev.map((plan) => {
        if (plan.id !== planId) return plan;
        const updateMap = new Map(unitIdsArray.map((u) => [u.id, u]));
        return {
          ...plan,
          unit_ids: plan.unit_ids.map((unit) =>
            updateMap.has(unit.id)
              ? { ...unit, ...updateMap.get(unit.id) }
              : unit,
          ),
        };
      }),
    );
  };

  return (
    <>
      <div className={styles.T2Container}>
        <div className={styles.RangeContainer}>
          <div className={styles.rangeBox}>
            <div
              className={styles.QualityValue}
              style={{
                left: `calc(${(quantity / rangeData[rangeData.length - 1]?.EndRange) * 100}% - 15px)`,
              }}
            >
              <input
                type="text"
                value={quantity}
                className={styles.qualityInput}
                onChange={(e) => handleQuantityChange(e)}
              />
            </div>
            <input
              type="range"
              min={rangeData[0]?.StartRange || 0}
              max={rangeData[rangeData.length - 1]?.EndRange}
              step={steps}
              value={quantity}
              className={styles.inputRange}
              onChange={handleQuantityChange}
            />
            <div className={styles.minmax}>
              <p>min: {rangeData[0]?.StartRange || 0}</p>
              <p>max: {rangeData[rangeData.length - 1]?.EndRange || "--"}</p>
            </div>
          </div>
          <T2RangeEdit unitPrices={rangeData} />
        </div>
        <div className={styles.heading}>Product price</div>
        <div className={styles.horizontalForm}>
          <div className={styles.AddCardForm}></div>
          {plans.length > 0 &&
            plans
              .filter(
                (item, id) =>
                  item.typeId === selected && item.interval === selectInterval,
              )
              .map((product, index) => {
                const filteredUnitIds =
                  product.unit_ids?.filter(
                    (u) => u.vendor_id === selectedVendor,
                  ) || [];
                return (
                  <>
                    {(product.price_type === "price" ||
                      !product.price_type) && (
                      <form key={product.id} className={styles.pricing_card}>
                        <div className={styles.pricing_card_circle_top_left} />
                        <div
                          className={styles.pricing_card_circle_bottom_right}
                        />

                        {/* Name */}
                        <div className={styles.pricing_card_title}>
                          <p>{product.name}</p>
                        </div>
                        {/* Description */}
                        <div className={styles.pricing_card_description}>
                          <p>{product.description || "—"}</p>
                        </div>
                        {/* Payment Type */}
                        <div className={styles.paymentRow}>
                          <div className={styles.AmountField}>
                            <input
                              type="text"
                              name="unit_ids"
                              readOnly
                              value={
                                filteredUnitIds[unitIndex]?.Marketplace_price *
                                  quantity || "--"
                              }
                              className={`${styles.amountInput}`}
                            />
                            <UnitPriceT2Form
                              key={product.id}
                              unitPrices={filteredUnitIds || []}
                              onSave={(updatedUnitPrices) =>
                                updatePlanUnitIds(product.id, updatedUnitPrices)
                              }
                            />
                          </div>
                          <div className={styles.paymentSelect}>
                            {product.currency}
                          </div>
                        </div>

                        {/* Tags */}
                        <div className={styles.pricing_card_subtext}>
                          {product.tags || "—"}
                        </div>

                        {/* Status & Region */}
                        <div className={styles.pricing_card_notes}>
                          <div className={styles.pricing_card_note}>
                            {product.status}
                          </div>
                          <div className={styles.pricing_card_note}>
                            <span>{product.region || "—"}</span>
                          </div>
                        </div>
                        {/* Trial & Modify Orders */}
                        <div className={styles.pricing_card_notes}>
                          <div className={styles.pricing_card_note}>
                            {product.trial_period_days
                              ? `${product.trial_period_days} days trial`
                              : "No Trial"}
                          </div>
                          <div className={styles.pricing_card_note}>
                            {product.modify_orders || "—"}
                          </div>
                        </div>
                        {/* unit_price type */}
                        <div className={styles.pricing_card_note}>
                          {product.unit_price_type || "—"}
                        </div>
                        {/* Price validity */}
                        <div className={styles.pricing_card_notes}>
                          <div className={styles.pricing_card_note}>
                            <p>
                              {product.price_validity_interval === "lifetime"
                                ? "No Expiry"
                                : `${product.n_price_validity_interval} ${product.price_validity_interval}(s)`}
                            </p>
                          </div>
                        </div>
                        {/* GST */}
                        <div className={styles.pricing_card_gst}>
                          GST extra applicable for inr pricing
                        </div>

                        <p className={styles.pricing_card_divider}></p>
                        {/* Highlights */}
                        <div className={styles.pricing_card_highlights_title}>
                          <p>Plan highlights</p>
                        </div>
                        <ul className={styles.pricing_card_features}>
                          {product.plan_highlight.map((highlight, index) => (
                            <li
                              key={index}
                              className={styles.pricing_card_feature_item}
                            >
                              <Check style={{ fontSize: "14px" }} />
                              {highlight}
                            </li>
                          ))}
                        </ul>
                      </form>
                    )}
                    {product.price_type === "contact" && (
                      <form key={product.id} className={styles.pricing_card}>
                        <div className={styles.pricing_card_circle_top_left} />
                        <div
                          className={styles.pricing_card_circle_bottom_right}
                        />
                        {/* Name */}
                        <div className={styles.pricing_card_title}>
                          <p>{product.name}</p>
                        </div>

                        {/* Description */}
                        <div className={styles.pricing_card_description}>
                          <p>{product.description || "—"}</p>
                        </div>

                        {/* readonly Payment Type */}
                        <div className={styles.paymentRow}>
                          <div className={styles.AmountField}>
                            <input
                              type="text"
                              readOnly
                              name="unit_ids"
                              value={"--"}
                              className={`${styles.amountInput}`}
                              placeholder="Enter Amount here"
                            />
                          </div>
                          <input
                            type="text"
                            name="currency"
                            readOnly
                            value={"--"}
                            className={styles.paymentSelect}
                            placeholder="Enter Currency"
                          />
                        </div>
                        {/* Tags */}
                        <div className={styles.pricing_card_subtext}>
                          {product.tags || "—"}
                        </div>

                        {/* Status & Region */}
                        <div className={styles.pricing_card_notes}>
                          <div className={styles.pricing_card_note}>
                            {product.status}
                          </div>
                          <div className={styles.pricing_card_note}>
                            <span>{product.region || "—"}</span>
                          </div>
                        </div>

                        {/* Trial & Modify Orders */}
                        <div className={styles.pricing_card_notes}>
                          <div className={styles.pricing_card_note}>
                            {product.trial_period_days
                              ? `${product.trial_period_days} days trial`
                              : "No Trial"}
                          </div>
                          <div className={styles.pricing_card_note}>
                            {product.modify_orders || "—"}
                          </div>
                        </div>

                        {/* unit_price type */}
                        <div className={styles.pricing_card_note}>
                          {product.unit_price_type || "—"}
                        </div>

                        {/* Price validity */}
                        <div className={styles.pricing_card_notes}>
                          <div className={styles.pricing_card_note}>
                            <p>
                              {product.price_validity_interval === "lifetime"
                                ? "No Expiry"
                                : `${product.n_price_validity_interval} ${product.price_validity_interval}(s)`}
                            </p>
                          </div>
                        </div>

                        {/* GST */}
                        <div className={styles.pricing_card_gst}>
                          GST extra applicable for INR pricing
                        </div>

                        <p className={styles.pricing_card_divider}></p>
                        {/* Highlights */}
                        <div className={styles.pricing_card_highlights_title}>
                          <p>Plan highlights</p>
                        </div>
                        <ul className={styles.pricing_card_features}>
                          {product.plan_highlight.map((highlight, index) => (
                            <li
                              key={index}
                              className={styles.pricing_card_feature_item}
                            >
                              <div className={styles.highlight_text}>
                                <Check style={{ fontSize: "14px" }} />
                                {highlight}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </form>
                    )}
                  </>
                );
              })}
        </div>
        <div className={styles.heading}>Features</div>
        <div className={styles.featuresGrid}>
          {features?.length > 0 &&
            features.map((feature, i) => (
              <div key={i} className={styles.featureCard}>
                <div style={{ width: "100%" }}>
                  {/* Feature Heading */}
                  <div className={styles.featureHeader}>
                    <div className={styles.inputHeading}>
                      {feature.name || "—"}
                    </div>
                  </div>
                </div>

                <div className={styles.rows}>
                  <div className={styles.rowContainer}>
                    {feature.data.map((d, j) => (
                      <div key={j} className={styles.row}>
                        {/* Feature Title */}
                        <div className={styles.inputtitle}>{d || "—"}</div>

                        {/* Plan Values */}
                        {plans.length > 0 &&
                          plans
                            .filter(
                              (item) =>
                                item.typeId === selected &&
                                item.interval === selectInterval,
                            )
                            .map((p) => {
                              const planFeature = p.features.find(
                                (pf) => pf.key === feature.key,
                              );

                              return (
                                <div key={p.id}>
                                  <div className={styles.input}>
                                    {planFeature?.data?.[j] || "—"}
                                  </div>
                                </div>
                              );
                            })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

export default MarketPlaceT2;
